<script setup>
import moment from "moment";
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import AlertMessage from "@/components/AlertMessage.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import DecaWizardProgress from "@/views/plans/components/wizard/DecaWizardProgress.vue";
import DecaWizardStepShipper from "@/views/plans/components/wizard/DecaWizardStepShipper.vue";
import DecaWizardStepTransport from "@/views/plans/components/wizard/DecaWizardStepTransport.vue";
import DecaWizardStepPackages from "@/views/plans/components/wizard/DecaWizardStepPackages.vue";
import DecaWizardStepDestination from "@/views/plans/components/wizard/DecaWizardStepDestination.vue";
import { decaStore } from "@/_store/decaStore";
import { thirdCarrierStore } from "@/_store/thirdCarrierStore";
import { errorUtils } from "@/utils";

const props = defineProps({
  modalId: { type: String, required: true },
  stopId: { type: String, required: true },
});

const emit = defineEmits(["close", "confirm"]);

const modalDecaReview = ref(null);
const vuexStore = useStore();
const bootstrap = vuexStore.state.bootstrap;
const decasStore = decaStore();
const thirdCarriersStore = thirdCarrierStore();

const snackbar = ref(null);
const isSubmitting = ref(false);
const isLoading = ref(true);
const loadError = ref("");
// Hay DeCA vigente para la parada (el backend devuelve el Deca ya
// persistido, con _id) => se esta modificando, no generando por primera vez.
const isModification = ref(false);
const decaVersion = ref(1);

const formSteps = 3;
const activeStep = ref(0);

const stopInfo = reactive({ number: "", date: "" });

const shipperName = ref("");
const shipperVatNumber = ref("");
const shipperAddress = ref("");

const carrierType = ref("PROPIO");
const carrierName = ref("");
const carrierVatNumber = ref("");
const carrierAddress = ref("");
const carrierSave = ref(false);
const carrierSuggestions = ref([]);
const lastCarrierNameQuery = ref(null);
const accountCarrierDefaults = reactive({ name: "", vat_number: "", address: "" });

const vehicleRegistration = ref("");
const vehicleTrailerRegistration = ref("");
const vehicleSpecialAuthorization = ref("");
const accountVehicleDefaults = reactive({
  registration_number: "",
  trailer_registration_number: "",
  special_authorization_number: "",
});

const destinationName = ref("");
const destinationAddress = ref("");
const observations = ref("");
const packages = ref([]);

const isTercero = computed(() => carrierType.value === "TERCERO");

// Al alternar propio/tercero se recarga transportista y vehiculo con el
// juego de valores correspondiente (nuestra cuenta/vehiculo de la ruta, u
// hoja en blanco para el tercero, que trae su propio vehiculo) — ambos
// siguen siendo editables despues, esto solo fija el punto de partida.
// Se omite durante la precarga inicial (loadPreload) porque ahi carrierType
// se fija a lo que ya estaba guardado, no a una eleccion del usuario.
let suppressCarrierTypeWatch = false;
watch(carrierType, (type) => {
  if (suppressCarrierTypeWatch) return;
  carrierSuggestions.value = [];
  carrierSave.value = false;
  if (type === "PROPIO") {
    carrierName.value = accountCarrierDefaults.name;
    carrierVatNumber.value = accountCarrierDefaults.vat_number;
    carrierAddress.value = accountCarrierDefaults.address;
    vehicleRegistration.value = accountVehicleDefaults.registration_number;
    vehicleTrailerRegistration.value =
      accountVehicleDefaults.trailer_registration_number;
    vehicleSpecialAuthorization.value =
      accountVehicleDefaults.special_authorization_number;
    lastCarrierNameQuery.value = carrierName.value;
  } else {
    carrierName.value = "";
    carrierVatNumber.value = "";
    carrierAddress.value = "";
    vehicleRegistration.value = "";
    vehicleTrailerRegistration.value = "";
    vehicleSpecialAuthorization.value = "";
    lastCarrierNameQuery.value = null;
  }
});

const rules = {
  shipperName: {
    required: helpers.withMessage(
      "La razón social del cargador es requerida",
      required
    ),
  },
  shipperVatNumber: {
    required: helpers.withMessage("El NIF del cargador es requerido", required),
  },
  shipperAddress: {
    required: helpers.withMessage(
      "El domicilio del cargador es requerido",
      required
    ),
  },
  carrierName: {
    required: helpers.withMessage(
      "La razón social del transportista es requerida",
      required
    ),
  },
  carrierVatNumber: {
    required: helpers.withMessage(
      "El NIF del transportista es requerido",
      required
    ),
  },
  carrierAddress: {},
  vehicleRegistration: {
    required: helpers.withMessage(
      "La matrícula del vehículo es requerida",
      required
    ),
  },
  destinationAddress: {
    required: helpers.withMessage(
      "La dirección de destino es requerida",
      required
    ),
  },
};
const v$ = useVuelidate(rules, {
  shipperName,
  shipperVatNumber,
  shipperAddress,
  carrierName,
  carrierVatNumber,
  carrierAddress,
  vehicleRegistration,
  destinationAddress,
});

function removePackage(item) {
  packages.value = packages.value.filter((row) => row !== item);
}

function addPackage(item) {
  packages.value = [...packages.value, item];
}

async function loadPreload() {
  isLoading.value = true;
  loadError.value = "";
  try {
    const result = await decasStore.preloadDeca(props.stopId);
    const data = result?.data || result || {};
    isModification.value = Boolean(data._id);
    decaVersion.value = data.version || 1;

    stopInfo.number = data.stop_number || "";
    stopInfo.date = data.stop_date
      ? moment(data.stop_date).format("DD/MM/YYYY")
      : "";

    shipperName.value = data.shipper?.name || "";
    shipperVatNumber.value = data.shipper?.vat_number || "";
    shipperAddress.value = data.shipper?.address || "";

    accountCarrierDefaults.name = data.carrier?.name || "";
    accountCarrierDefaults.vat_number = data.carrier?.vat_number || "";
    accountCarrierDefaults.address = data.carrier?.address || "";

    accountVehicleDefaults.registration_number =
      data.vehicle?.registration_number || "";
    accountVehicleDefaults.trailer_registration_number =
      data.vehicle?.trailer_registration_number || "";
    accountVehicleDefaults.special_authorization_number =
      data.vehicle?.special_authorization_number || "";

    // carrier_type/carrier/vehicle son lo guardado en el DeCA vigente al
    // modificar, o los valores por defecto (PROPIO/cuenta/vehiculo de ruta)
    // al generar por primera vez — en ambos casos se muestran tal cual.
    suppressCarrierTypeWatch = true;
    carrierType.value = data.carrier_type || "PROPIO";
    carrierName.value = accountCarrierDefaults.name;
    carrierVatNumber.value = accountCarrierDefaults.vat_number;
    carrierAddress.value = accountCarrierDefaults.address;
    vehicleRegistration.value = accountVehicleDefaults.registration_number;
    vehicleTrailerRegistration.value =
      accountVehicleDefaults.trailer_registration_number;
    vehicleSpecialAuthorization.value =
      accountVehicleDefaults.special_authorization_number;
    lastCarrierNameQuery.value = carrierName.value;
    nextTick(() => {
      suppressCarrierTypeWatch = false;
    });

    destinationName.value = data.destination_name || "";
    destinationAddress.value = data.destination_address || "";
    observations.value = data.observations || "";
    packages.value = Array.isArray(data.packages)
      ? data.packages.map((item) => ({ ...item }))
      : [];
  } catch (error) {
    loadError.value = errorUtils.getErrorMessageHtml(
      error,
      "No se pudieron cargar los datos del DeCA."
    );
  } finally {
    isLoading.value = false;
  }
}

async function onCarrierNameInput(text) {
  const query = String(text || "").trim();
  if (query === lastCarrierNameQuery.value) return;
  lastCarrierNameQuery.value = query;

  carrierSave.value = false;
  try {
    const results = await thirdCarriersStore.searchThirdCarriers(query);
    carrierSuggestions.value = Array.isArray(results) ? results : [];
  } catch {
    carrierSuggestions.value = [];
  }
}

function selectCarrierSuggestion(suggestion) {
  carrierName.value = suggestion?.name || "";
  lastCarrierNameQuery.value = carrierName.value;
  carrierVatNumber.value = suggestion?.vat_number || "";
  carrierAddress.value = suggestion?.address || "";
  carrierSuggestions.value = [];
}

function closeSnackbar() {
  snackbar.value = null;
}

function showError(message) {
  snackbar.value = {
    title: "Error",
    message: message || "No se pudo generar el DeCA.",
    type: "danger",
    color: "white",
  };
}

function formatValidationErrors(errors) {
  let message = "";
  for (const error of errors) {
    if (!message.includes(`<li>${error.$message}</li>`)) {
      message = `${message}<li>${error.$message}</li>`;
    }
  }
  return `<ul>${message}</ul>`;
}

function showValidationErrors(errors) {
  snackbar.value = {
    title: "Error",
    message: formatValidationErrors(errors),
    type: "danger",
    color: "white",
  };
}

async function validateFields(fields = []) {
  const errors = [];
  for (const field of fields) {
    const fieldRef = v$.value?.[field];
    if (!fieldRef) continue;
    await fieldRef.$validate();
    if (fieldRef.$error) {
      errors.push(...fieldRef.$errors);
    }
  }
  if (errors.length > 0) {
    showValidationErrors(errors);
  }
  return errors;
}

function handleHide() {
  isSubmitting.value = false;
  emit("close");
}

function toNumber(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

const packagesTouched = ref(false);
const packagesStepRef = ref(null);

function isPackageFieldEmpty(item, field) {
  if (field === "weight") return !(toNumber(item.weight) > 0);
  return !String(item[field] || "").trim();
}

function hasPackageErrors() {
  return packages.value.some(
    (item) =>
      isPackageFieldEmpty(item, "handling_type") ||
      isPackageFieldEmpty(item, "content_description") ||
      isPackageFieldEmpty(item, "origin_address") ||
      isPackageFieldEmpty(item, "weight")
  );
}

function nextStep() {
  if (activeStep.value < formSteps) {
    activeStep.value += 1;
  }
}

function prevStep() {
  if (activeStep.value > 0) {
    activeStep.value -= 1;
  }
}

async function goToTransportStep() {
  const errors = await validateFields([
    "shipperName",
    "shipperVatNumber",
    "shipperAddress",
  ]);
  if (errors.length > 0) return;

  nextStep();
}

async function goToPackagesStep() {
  const errors = await validateFields([
    "carrierName",
    "carrierVatNumber",
    "vehicleRegistration",
  ]);
  if (errors.length > 0) return;

  nextStep();
}

function goToDestinationStep() {
  if (packagesStepRef.value?.isEditingAny()) {
    showError("Confirma o cancela el bulto que estás editando antes de continuar.");
    return;
  }
  if (hasPackageErrors()) {
    packagesTouched.value = true;
    showError(
      "Revisa los bultos: manipulación, naturaleza, origen y peso son obligatorios."
    );
    return;
  }

  nextStep();
}

async function confirmReview() {
  if (isSubmitting.value || isLoading.value || loadError.value) return;

  await v$.value.$validate();

  if (v$.value.shipperName.$error) {
    activeStep.value = 0;
    showError(v$.value.shipperName.$errors[0].$message);
    return;
  }
  if (v$.value.shipperVatNumber.$error) {
    activeStep.value = 0;
    showError(v$.value.shipperVatNumber.$errors[0].$message);
    return;
  }
  if (v$.value.shipperAddress.$error) {
    activeStep.value = 0;
    showError(v$.value.shipperAddress.$errors[0].$message);
    return;
  }
  if (v$.value.carrierName.$error) {
    activeStep.value = 1;
    showError(v$.value.carrierName.$errors[0].$message);
    return;
  }
  if (v$.value.carrierVatNumber.$error) {
    activeStep.value = 1;
    showError(v$.value.carrierVatNumber.$errors[0].$message);
    return;
  }
  if (v$.value.vehicleRegistration.$error) {
    activeStep.value = 1;
    showError(v$.value.vehicleRegistration.$errors[0].$message);
    return;
  }
  if (packagesStepRef.value?.isEditingAny()) {
    activeStep.value = 2;
    showError("Confirma o cancela el bulto que estás editando antes de guardar.");
    return;
  }
  if (hasPackageErrors()) {
    packagesTouched.value = true;
    activeStep.value = 2;
    showError(
      "Revisa los bultos: manipulación, naturaleza, origen y peso son obligatorios."
    );
    return;
  }
  if (v$.value.destinationAddress.$error) {
    activeStep.value = 3;
    showError(v$.value.destinationAddress.$errors[0].$message);
    return;
  }

  isSubmitting.value = true;
  closeSnackbar();

  try {
    const payload = {
      shipper: {
        name: shipperName.value.trim(),
        vat_number: shipperVatNumber.value.trim(),
        address: shipperAddress.value.trim(),
      },
      carrier: {
        type: carrierType.value,
        name: carrierName.value.trim(),
        vat_number: carrierVatNumber.value.trim(),
        address: carrierAddress.value.trim(),
        save: isTercero.value ? !!carrierSave.value : false,
      },
      vehicle: {
        registration_number: vehicleRegistration.value.trim(),
        trailer_registration_number: vehicleTrailerRegistration.value.trim(),
        special_authorization_number: vehicleSpecialAuthorization.value.trim(),
      },
      destination_name: destinationName.value.trim(),
      destination_address: destinationAddress.value.trim(),
      observations: observations.value.trim(),
      packages: packages.value.map((item) => ({
        task_id: item.task_id,
        barcode: String(item.barcode || "").trim(),
        content_description: String(item.content_description || "").trim(),
        handling_type: String(item.handling_type || "").trim(),
        weight: toNumber(item.weight),
        volumetric_weight: toNumber(item.volumetric_weight),
        origin_address: String(item.origin_address || "").trim(),
      })),
    };

    await decasStore.generateDeca(props.stopId, payload);
    if (decasStore.error) throw decasStore.error;

    isSubmitting.value = false;
    emit("confirm");
    modalDecaReview.value?.closeModal();
  } catch (error) {
    isSubmitting.value = false;
    showError(
      errorUtils.getErrorMessageHtml(
        error,
        "No se pudo generar el DeCA. Revisa los datos e inténtalo de nuevo."
      )
    );
  }
}

onMounted(() => {
  loadPreload();
  const modal = new bootstrap.Modal(document.getElementById(props.modalId), {
    keyboard: true,
    backdrop: "static",
  });
  modal.show();
});
</script>

<template>
  <modal-base
    :id="modalId"
    ref="modalDecaReview"
    width="60rem"
    min-height="24rem"
    body-variant="canvas"
    @hide="handleHide"
  >
    <template #header>
      <h6 class="mb-0">
        {{ isModification ? "Modificar DeCA" : "Generar DeCA" }}
      </h6>
    </template>
    <template #body>
      <div v-if="isLoading" class="deca-loading-state">Cargando datos...</div>

      <alert-message v-else-if="loadError" title="Error" type="danger" class="mb-3">
        <span v-html="loadError"></span>
      </alert-message>

      <div v-else class="multisteps-form">
        <deca-wizard-progress :active-step="activeStep" />

        <div class="row">
          <div class="m-auto col-12">
            <form class="multisteps-form__form card-detail-layout text-start">
              <deca-wizard-step-shipper
                v-show="activeStep === 0"
                :stop-date="stopInfo.date"
                :stop-number="stopInfo.number"
                :is-modification="isModification"
                :deca-version="decaVersion"
                :shipper-name="shipperName"
                :shipper-name-error="v$.shipperName.$error"
                :shipper-vat-number="shipperVatNumber"
                :shipper-vat-number-error="v$.shipperVatNumber.$error"
                :shipper-address="shipperAddress"
                :shipper-address-error="v$.shipperAddress.$error"
                @update:shipper-name="shipperName = $event"
                @update:shipper-vat-number="shipperVatNumber = $event"
                @update:shipper-address="shipperAddress = $event"
                @next="goToTransportStep"
              />

              <deca-wizard-step-transport
                v-show="activeStep === 1"
                :carrier-type="carrierType"
                :carrier-name="carrierName"
                :carrier-name-error="v$.carrierName.$error"
                :carrier-suggestions="carrierSuggestions"
                :carrier-vat-number="carrierVatNumber"
                :carrier-vat-number-error="v$.carrierVatNumber.$error"
                :carrier-address="carrierAddress"
                :carrier-save="carrierSave"
                :vehicle-registration="vehicleRegistration"
                :vehicle-registration-error="v$.vehicleRegistration.$error"
                :vehicle-trailer-registration="vehicleTrailerRegistration"
                :vehicle-special-authorization="vehicleSpecialAuthorization"
                @update:carrier-type="carrierType = $event"
                @update:carrier-name="carrierName = $event"
                @update:carrier-vat-number="carrierVatNumber = $event"
                @update:carrier-address="carrierAddress = $event"
                @update:carrier-save="carrierSave = $event"
                @update:vehicle-registration="vehicleRegistration = $event"
                @update:vehicle-trailer-registration="vehicleTrailerRegistration = $event"
                @update:vehicle-special-authorization="vehicleSpecialAuthorization = $event"
                @carrier-name-input="onCarrierNameInput"
                @select-carrier-suggestion="selectCarrierSuggestion"
                @prev="prevStep"
                @next="goToPackagesStep"
              />

              <deca-wizard-step-packages
                ref="packagesStepRef"
                v-show="activeStep === 2"
                :packages="packages"
                :packages-touched="packagesTouched"
                @remove-package="removePackage"
                @add-package="addPackage"
                @prev="prevStep"
                @next="goToDestinationStep"
              />

              <deca-wizard-step-destination
                v-show="activeStep === 3"
                :is-active="activeStep === 3"
                :destination-name="destinationName"
                :destination-address="destinationAddress"
                :destination-address-error="v$.destinationAddress.$error"
                :observations="observations"
                :is-submitting="isSubmitting"
                :is-modification="isModification"
                :disable-submit="isSubmitting || isLoading || !!loadError"
                @update:destination-name="destinationName = $event"
                @update:destination-address="destinationAddress = $event"
                @update:observations="observations = $event"
                @prev="prevStep"
                @submit="confirmReview"
              />
            </form>
          </div>
        </div>
      </div>
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>

<style scoped>
.deca-loading-state {
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  padding: 2rem 0;
  text-align: center;
}
</style>
